import * as React from 'react';
import { Link } from 'react-router-dom';
import { handleInputChangeWrapper } from '../../helpers/state';
import { login, passwordReset, activate, passwordResetRequest } from '../../services/account';
import { History } from 'history';
import {
    defineMessages,
    InjectedIntlProps,
    injectIntl,
    FormattedHTMLMessage,
    FormattedMessage,
} from 'react-intl';

import menuImage from '../../images/xbox-menu.png';
import logoImage from '../../images/logo-3.png';
import { ABOUT_PATH, CONTACT_PATH } from '../../constants';
import Footer from '../shared/Footer';

interface IProps {
    history: History;
    match?: { params: { token: string } };
    reset?: boolean;
}
interface IState {
    username: string;
    password: string;
    passwordConfirmed: string;
    error: string | null;
    passwordResetRequestMode: boolean;
    passwordResetMode: boolean;
    message: string | null;
}

class Index extends React.Component<IProps & InjectedIntlProps, IState> {
    state = {
        username: '',
        password: '',
        passwordConfirmed: '',
        error: null,
        passwordResetRequestMode: false,
        passwordResetMode: false,
        message: null,
    };

    messages = defineMessages({
        loginError: {
            id: 'login.error',
            defaultMessage:
                'There was an error logging in. Please check your username and password.',
        },
        missingUsername: {
            id: 'login.error.username',
            defaultMessage: 'Your email is required.',
        },
        checkEmail: {
            id: 'login.message.checkemail',
            defaultMessage: 'A link to reset your password has been sent to your email.',
        },
        checkEmailError: {
            id: 'login.message.checkemail.error',
            defaultMessage: 'Unable to find username.',
        },
        token: {
            id: 'login.message.token',
            defaultMessage: 'Your account is now active. Log in to continue.',
        },
        tokenError: {
            id: 'login.message.token.error',
            defaultMessage: 'Invalid token. Please use the link sent to your email.',
        },
        passwordResetMatch: {
            id: 'login.reset.error.pwdMatch',
            defaultMessage: 'Please verify that your passwords match.',
        },
        passwordLengthError: {
            id: 'login.reset.error.pwdLen',
            defaultMessage: 'Passwords must be at least 8 charaters long.',
        },
        passwordResetServerError: {
            id: 'login.message.passwordreset.server.error',
            defaultMessage:
                'There was an error completing your request. Please reload and try again.',
        },
        passwordResetSuccess: {
            id: 'login.message.passwordreset.success',
            defaultMessage: 'Your password has been reset. Please log in using your new password.',
        },
        badResetToken: {
            id: 'login.reset.token.notFound',
            defaultMessage: 'The secure reset token has expired or could not be found.',
        },
        retryLink: {
            id: 'login.reset.token.retry',
            defaultMessage: 'Click here to try again',
        },
    });

    handleInputChange = handleInputChangeWrapper(this);

    async componentDidMount() {
        if (this.props.match) {
            const { token } = this.props.match.params;
            if (token && !this.props.reset) {
                const result = await activate(token);
                if (result.success)
                    this.setState({
                        message: this.props.intl.formatMessage(this.messages.token),
                        error: null,
                    });
                else
                    this.setState({
                        error: this.props.intl.formatMessage(this.messages.tokenError),
                        message: null,
                    });
            } else if (token) {
                this.setState({ passwordResetMode: true });
            }
        }
    }

    showRequestReset = () => {
        this.setState({
            passwordResetMode: false,
            passwordResetRequestMode: true,
            message: null,
            error: null,
        });
    };

    showResetPassword = () => {
        this.setState({
            passwordResetMode: true,
            passwordResetRequestMode: false,
            message: null,
            error: null,
            password: '',
            passwordConfirmed: '',
        });
    };

    showLogin = () => {
        this.setState({
            passwordResetMode: false,
            passwordResetRequestMode: false,
            message: null,
            error: null,
            username: '',
            password: '',
        });
    };

    handleRequestReset = async () => {
        if (!this.state.username.trim()) {
            return this.setState({
                error: this.props.intl.formatMessage(this.messages.missingUsername),
            });
        }
        const result = await passwordResetRequest(`${this.state.username}`?.toLocaleLowerCase());

        if (result.success) {
            this.setState({
                message: this.props.intl.formatMessage(this.messages.checkEmail),
                error: null,
                passwordResetRequestMode: false,
            });
        } else {
            const msg = result.message
                ? result.message
                : this.props.intl.formatMessage(this.messages.checkEmailError);
            this.setState({ error: msg, message: null });
        }
    };

    handleReset = async () => {
        if (
            !this.state.password ||
            !this.state.passwordConfirmed ||
            this.state.password.length < 8 ||
            this.state.passwordConfirmed.length < 8
        ) {
            this.setState({
                error: this.props.intl.formatMessage(this.messages.passwordLengthError),
                message: null,
            });
        } else if (this.state.password !== this.state.passwordConfirmed) {
            this.setState({
                error: this.props.intl.formatMessage(this.messages.passwordResetMatch),
                message: null,
            });
        } else if (this.props.match && this.props.match.params.token) {
            const result = await passwordReset(this.props.match.params.token, this.state.password);
            if (result.success) {
                this.setState({
                    message: this.props.intl.formatMessage(this.messages.passwordResetSuccess),
                    error: null,
                    passwordResetMode: false,
                    passwordResetRequestMode: false,
                });
            } else {
                let msg = result.message
                    ? result.message
                    : this.props.intl.formatMessage(this.messages.passwordResetServerError);
                if (msg.match(/no such token/gi)) {
                    msg = (
                        <React.Fragment>
                            {this.props.intl.formatMessage(this.messages.badResetToken)}{' '}
                            <button
                                style={{ display: 'inline', verticalAlign: 'inherit', padding: 0 }}
                                className="btn btn-link"
                                onClick={() => this.showRequestReset()}>
                                {this.props.intl.formatMessage(this.messages.retryLink)}
                            </button>
                            .
                        </React.Fragment>
                    );
                }
                this.setState({ error: msg, message: null });
            }
        }
    };

    handleKeyDown = (e: any) => {
        if (e.key === 'Enter') {
            this.handleLogin();
        }
    };

    handleLogin = async () => {
        try {
            const result = await login(`${this.state.username}`?.toLocaleLowerCase(), this.state.password);
            if (result.success) {
                this.props.history.push('/client/dashboard');
            } else {
                if (this.state.error)
                    this.setState({ error: null }, () => this.setState({ error: result.message }));
                else this.setState({ error: result.message });
            }
        } catch (ex) {
            this.setState({
                error: this.props.intl.formatMessage(this.messages.loginError),
            });
        }
    };

    renderPasswordField = () => {
        return (
            <React.Fragment>
                <div className="logininput">
                    <label>
                        <FormattedMessage id="login.form.pwd.label" defaultMessage="Password" />
                    </label>
                    <input
                        value={this.state.password}
                        name="password"
                        type="password"
                        onChange={this.handleInputChange}
                        onKeyDown={this.handleKeyDown}
                    />
                </div>
            </React.Fragment>
        );
    };

    renderPasswordConfirmField = () => {
        return (
            <div className="logininput">
                <label>
                    <FormattedMessage id="login.form.pwd.confirm.label" defaultMessage="Confirm" />
                </label>
                <input
                    value={this.state.passwordConfirmed}
                    name="passwordConfirmed"
                    type="password"
                    onChange={this.handleInputChange}
                    onKeyDown={this.handleKeyDown}
                />
            </div>
        );
    };

    renderUsername = () => {
        return (
            <div className="logininput">
                <label>
                    <FormattedMessage id="login.form.user.label" defaultMessage="Email" />
                </label>
                <input
                    value={this.state.username}
                    name="username"
                    onChange={this.handleInputChange}
                    onKeyDown={this.handleKeyDown}
                    type="text"
                />
            </div>
        );
    };

    renderMessages = () => {
        // TODO error & message should not co-exist but may need to handle better
        if (this.state.error) {
            return <div className="alert alert-danger p-2">{this.state.error}</div>;
        }
        if (this.state.message) {
            return <div className="alert alert-success p-2">{this.state.message}</div>;
        }
        return null;
    };

    renderForm = () => {
        if (this.state.passwordResetMode) {
            return this.renderPasswordReset();
        } else if (this.state.passwordResetRequestMode) {
            return this.renderRequestReset();
        } else {
            return this.renderLogin();
        }
    };

    renderLogin = () => {
        return (
            <React.Fragment>
                <h4>
                    <FormattedMessage id="login.form.hdr" defaultMessage="Login" />
                </h4>
                {this.renderMessages()}
                {this.renderUsername()}
                {this.renderPasswordField()}
                <div className="row">
                    <button
                        onClick={this.handleLogin}
                        className="btn btn-outline-primary"
                        style={{ marginLeft: 100 }}>
                        <FormattedMessage id="login.form.button.login" defaultMessage="Login" />
                    </button>
                    <Link to="/client/signup" className="btn btn-link pt-2 text-white">
                        <b>
                            <FormattedMessage id="login.cta.button" defaultMessage="Sign Up" />
                        </b>
                    </Link>
                </div>
                <div className="logininput">
                    <button className="btn btn-link" onClick={() => this.showRequestReset()}>
                        <FormattedMessage
                            id="login.form.forgot.pwd"
                            defaultMessage="Forgot Password?"
                        />
                    </button>
                </div>
            </React.Fragment>
        );
    };

    renderRequestReset = () => {
        return (
            <React.Fragment>
                <h4>
                    <FormattedMessage
                        id="login.form.passwordreset.hdr"
                        defaultMessage="Reset Password"
                    />
                </h4>
                <p>
                    <FormattedMessage
                        id="login.form.passwordresetreq.txt"
                        defaultMessage="Enter the email you registered as your login name to receive a secure link to reset your password."
                    />
                </p>
                {this.renderMessages()}
                {this.renderUsername()}
                <button onClick={this.handleRequestReset} className="btn btn-outline-primary">
                    <FormattedMessage
                        id="login.form.button.resetSend"
                        defaultMessage="Send Reset Request"
                    />
                </button>
                <div className="logininput">
                    <button className="btn btn-link" onClick={() => this.showLogin()}>
                        <i className="fas fa-angle-left" />
                        &nbsp;
                        <FormattedMessage
                            id="login.form.forgot.login"
                            defaultMessage="Back to Login"
                        />
                    </button>
                </div>
            </React.Fragment>
        );
    };

    renderPasswordReset = () => {
        return (
            <React.Fragment>
                <h4>
                    <FormattedMessage
                        id="login.form.passwordreset.hdr"
                        defaultMessage="Reset Password"
                    />
                </h4>
                <p>
                    <FormattedMessage
                        id="login.form.passwordreset.txt"
                        defaultMessage="Enter a new password. Passwords must be at least 8 characters long."
                    />
                </p>
                {this.renderMessages()}
                {this.renderPasswordField()}
                {this.renderPasswordConfirmField()}
                <button onClick={this.handleReset} className="btn btn-outline-primary">
                    <FormattedMessage id="login.form.button.submit" defaultMessage="Submit" />
                </button>
                <div className="logininput">
                    <button className="btn btn-link" onClick={() => this.showLogin()}>
                        <i className="fas fa-angle-left" />
                        &nbsp;
                        <FormattedMessage
                            id="login.form.forgot.login"
                            defaultMessage="Back to Login"
                        />
                    </button>
                </div>
            </React.Fragment>
        );
    };

    render() {
        return (
            <section className="loginPage">
                <div className="side-menu-overlay" style={{ width: '0px', opacity: 0 }} />
                <div className="side-menu-wrapper">
                    <a href="#" className="menu-close">
                        &times;
                    </a>
                    <ul>
                        <li key="nav1">
                            <a href="#">
                                <FormattedMessage id="login.menu.home" defaultMessage="Home" />
                            </a>
                        </li>
                        <li key="nav2">
                            <a href="#">
                                <FormattedMessage
                                    id="login.menu.search"
                                    defaultMessage="Resource Search"
                                />
                            </a>
                        </li>
                    </ul>
                </div>
                <header className="topHeader">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-5 col-md-3 col-sm-12 logo">
                                <img src={logoImage} height="60" />
                            </div>
                            <div className="col-lg-7 col-md-9 col-sm-12 topRight">
                                <div className="topItem">
                                    <ul>
                                        <li key="nav3">
                                            <a href={ABOUT_PATH}>
                                                <FormattedMessage
                                                    id="login.nav.about"
                                                    defaultMessage="About Us"
                                                />
                                            </a>
                                        </li>
                                        <li key="nav4">
                                            <a href={CONTACT_PATH}>
                                                <FormattedMessage
                                                    id="login.nav.contact"
                                                    defaultMessage="Contact Us"
                                                />
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>
                <section className="logincontent">
                    <div className="container">
                        <div className="row">
                            <div
                                className="col-lg-6 col-md-12 col-sm-12"
                                style={{ marginBottom: 120 }}>
                                <div
                                    className="leftlogingon p-3"
                                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.30)' }}>
                                    <h4 className="text-white font-weight-bold">
                                        Harmonize® & Community Schools
                                    </h4>
                                    <p className="text-white">
                                        Harmonize® can be an excellent software companion to your existing or new community school.
                                        Harmonize® helps community schools by: (1) providing coordinators with an easy-to-use tool to
                                        help manage community partnerships and resources; (2) helping coordinators easily maintain
                                        directories or libraries of school- and community-based resources; (3) supporting asset
                                        mapping of community and school resources; (4) facilitating a collaborative environment
                                        through in-app communication between schools and their partner network; and (5) serving as
                                        a resource directory, allowing administrators, educators, parents/caregivers, and students
                                        find and connect with vetted resources.


                                        Please contact us at{' '}

                                        <a
                                            className="font-weight-bold text-white"
                                            href="tel:3055350914">
                                            (305)-535-0914
                                        </a>{' '}
                                        or by email at{' '}
                                        <a
                                            className="font-weight-bold text-white"
                                            href="mailto:info@myharmonize.com">
                                            info@myharmonize.com
                                        </a>
                                        .
                                    </p>
                                </div>
                            </div>
                            <div className="col-lg-6 col-md-12 col-sm-12 order-first order-lg-last">
                                <div
                                    className="loginform"
                                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.30)' }}>
                                    {this.renderForm()}
                                    {/* <h3 >
                                        <FormattedHTMLMessage id="login.cta.txt" defaultMessage="It’s time to tune in to Harmonize®" />
                                    </h3> */}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <Footer history={this.props.history} />
            </section>
        );
    }
}

export default injectIntl(Index);



// WEBPACK FOOTER //
// ./src/client/login/Login.tsx